import { SizeType } from 'antd/es/config-provider/SizeContext';
import React, { FC, useState, useEffect, RefObject } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Approval } from 'api/approval';
import { ReactComponent as NoContentSvg } from 'assets/svgs/empty-states/no-content-1.svg';
import { Empty } from 'components/atoms/empty';
import { ResourceSectionType } from 'data/documents';
import { Task } from 'data/tasks';
import { StatusField } from 'stores/fields';
import { InsertResourcelistCommandOptions } from 'vendor/ckeditor/plugins/resource-list-section/types';
import { ApprovalTable } from './approval-table';
import { RemoveTask } from './remove-task';
import { ResourceHead } from './resource-head';
import styles from './resource-list-table.module.css';
import { TaskTable } from './task-table';

export interface ResourceListSectionConfig
  extends InsertResourcelistCommandOptions {
  originalResourceIds: number[];
  resourceSectionId: number;
}

interface Props {
  size?: SizeType;
  projectId: number;
  data: Task[] | Approval[];
  refTo?: RefObject<HTMLDivElement>;
  config: ResourceListSectionConfig;
  statusField: StatusField;
}

const Container = styled.div`
  margin-bottom: var(--spacing-07);
  margin-top: var(--spacing-05);
`;

const List = ({
  refTo,
  config,
  resourceSectionType,
  data,
  size,
  renderExtraActions,
  statusField,
}) => {
  return (
    <Container ref={refTo}>
      <ResourceHead config={config} statusField={statusField} />
      {resourceSectionType === ResourceSectionType.Task ||
      resourceSectionType === ResourceSectionType.Milestone
        ? data.length > 0 && (
            <TaskTable
              data={data as Task[]}
              size={size}
              renderExtraActions={renderExtraActions}
            />
          )
        : data.length > 0 && (
            <ApprovalTable
              data={data as Approval[]}
              size={size}
              renderExtraActions={renderExtraActions}
            />
          )}
    </Container>
  );
};

export const ResourceList: FC<Props> = (props) => {
  const { size = 'middle', data = [], refTo, config, statusField } = props;
  const { resourceSectionId, resourceSectionType } = config;

  const [canShowEmpty, setCanShowEmpty] = useState<boolean>(!data.length);

  useEffect(() => {
    setCanShowEmpty(!data.length);
  }, [data.length]);

  const renderExtraActions = (task: Task) => {
    return (
      <RemoveTask
        task={task}
        className={`${styles.remove_task} resource_list__remove_task_btn`}
        resourceSectionId={resourceSectionId}
      />
    );
  };

  return !canShowEmpty ? (
    <List
      refTo={refTo}
      config={config}
      resourceSectionType={resourceSectionType}
      data={data}
      size={size}
      statusField={statusField}
      renderExtraActions={renderExtraActions}
    />
  ) : (
    <>
      <ResourceHead config={config} statusField={statusField} />
      <Empty>
        <FormattedMessage id="status_updates.no_items" />
        <NoContentSvg />
      </Empty>
    </>
  );
};
